import { useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';

import {
  RefundIneligibilityReason,
  useCheckRefundEligibilityQuery,
} from '../../generated/graphql-gateway';

import {
  IUseCheckOrderEligibilityResponse,
  IUseRefundEligibilityInput,
  IUseRefundEligibilityResponse,
} from './types';

/*
 * Determines whether or not a user and the order permit performing
 * a customer initiated refund.
 *
 * Includes details about the refund eligibility along with
 * reason text for messaging.
 */
export const useRefundEligibility = ({
  orderId,
}: IUseRefundEligibilityInput): IUseRefundEligibilityResponse => {
  const { ineligibilityReason, isDeterminingRefundEligibility } = useCheckOrderEligibilityQuery(
    orderId
  );

  const refundIneligibilityReason = useRefundIneligibilityReason(ineligibilityReason);

  const isDeliveryActive = ineligibilityReason === RefundIneligibilityReason.ORDER_BEING_DELIVERED;

  const result: IUseRefundEligibilityResponse = useMemo(
    () => ({
      isDeterminingRefundEligibility,
      refundIneligibilityReason,
      isDeliveryActive,
    }),
    [isDeterminingRefundEligibility, refundIneligibilityReason, isDeliveryActive]
  );

  useEffect(() => {
    if (!isDeterminingRefundEligibility) {
      logEvent(CustomEventNames.REFUND_ELIGIBILITY_CHECK, EventTypes.Other, {
        message: `Refund eligibility check results`,
        orderId,
        ineligibilityReason,
        ...result,
      });
    }
  }, [ineligibilityReason, isDeterminingRefundEligibility, orderId, result]);

  return result;
};

const useCheckOrderEligibilityQuery = (
  orderId?: string | null
): IUseCheckOrderEligibilityResponse => {
  const { data, loading } = useCheckRefundEligibilityQuery({
    variables: { rbiOrderId: orderId ?? '' },
    skip: !orderId,
  });

  return {
    ineligibilityReason: data?.checkRefundEligibility?.ineligibilityReason,
    isDeterminingRefundEligibility: loading,
  };
};

// Messaging to be displayed on the support form if the user is not allowed to obtain a refund.
const useRefundIneligibilityReason = (
  ineligibilityReason?: RefundIneligibilityReason | null
): string | false => {
  const { formatMessage } = useIntl();

  const message = useMemo(() => {
    switch (ineligibilityReason) {
      case RefundIneligibilityReason.ALREADY_REFUNDED:
        return formatMessage({ id: 'supportFormOrderAlreadyRefunded' });
      case RefundIneligibilityReason.ORDER_BEING_DELIVERED:
        return formatMessage({ id: 'supportFormDeliveryActive' });
      default:
        return formatMessage({ id: 'supportFormRefundGenericError' });
    }
  }, [ineligibilityReason, formatMessage]);

  useLogUserErrorMessage({ message: message as string });

  return message;
};
